/* eslint-disable no-nested-ternary */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  MdLocationOn,
  MdAccessTime,
  MdLocalShipping,
  MdDownload,
  MdUploadFile,
  MdDelete,
} from 'react-icons/md';
import { FormPage } from '../../tailwindComponents/FormPage';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Autocomplete } from '../../tailwindComponents/Autocomplete';
import { Dropzone } from '../../tailwindComponents/Dropzone';
import { Button } from '../../tailwindComponents/Button';
import { Checkbox } from '../../tailwindComponents/Checkbox';
import {
  LoadSizeMatrixFields,
  DEFAULT_PALLET_RATES,
  DEFAULT_WEIGHT_RATES,
} from './LoadSizeMatrixFields';
import { buildMileageRateSheetFormData } from './mileageRateSheetUpload';
import { useAuthHttp } from '../../hooks';
import {
  POSTCODE_RATECARD_CREATE,
  DAILY_RATECARD_CREATE,
  ADDITIONAL_FTL_RATECARD_CREATE,
  MILEAGE_RATECARD_CREATE,
  MILEAGE_RATECARD_IMPORT,
  HERE_APIKEY,
} from '../../constants';
import { OFF_ROUTE_DISTANCE_UNIT } from '../../constants/ratecards';
import { fleetMapping } from '../PriceMaster/types';

const CURRENCY_OPTIONS = [
  { label: 'GBP', value: 'GBP' },
  { label: 'EUR', value: 'EUR' },
  { label: 'USD', value: 'USD' },
];

const TARIFF_TYPE_OPTIONS = [
  { label: 'Postcode RateCard', value: 'Postcode' },
  { label: 'Additional Load Charge', value: 'Additional FTL' },
  { label: 'Day Rate', value: 'Day Rate' },
  { label: 'Price Per Mile', value: 'Price Per Mile' },
];

// Base schema for all tariff types
const baseSchema = Yup.object().shape({
  tariffType: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required('Tariff type is required'),
  vehicleType: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required('Vehicle type is required'),
  transportBaseAddress: Yup.string()
    .required('Transport base address is required')
    .min(3, 'Transport base address must be at least 3 characters'),
  location: Yup.object().shape({
    lon: Yup.number(),
    lat: Yup.number(),
  }),
  currency: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .required('Currency is required'),
});

// Day Rate specific schema
const dayRateSchema = baseSchema.shape({
  chargeForEightHourDay: Yup.number()
    .required('Charge for an 8 hour day is required')
    .typeError('Must be a number')
    .moreThan(0, 'Must be greater than 0'),
  hourlyChargeOverEightHours: Yup.number()
    .required('Hourly charge over 8 hours is required')
    .typeError('Must be a number')
    .moreThan(0, 'Must be greater than 0'),
  hourlyChargeOverTenHours: Yup.number()
    .required('Hourly charge over 10 hours is required')
    .typeError('Must be a number')
    .moreThan(0, 'Must be greater than 0'),
});

const hasRealCoordinates = (location?: { lat?: number; lon?: number }) => {
  const lat = location?.lat ?? 0;
  const lon = location?.lon ?? 0;
  return (
    Math.abs(lat) <= 90 && Math.abs(lon) <= 180 && !(lat === 0 && lon === 0)
  );
};

const pickedTransportBaseLocationSchema = Yup.object()
  .shape({
    lon: Yup.number(),
    lat: Yup.number(),
  })
  .test(
    'picked-from-suggestions',
    'Pick the transport base address from the suggestions.',
    function isPickedFromSuggestions(location) {
      const { transportBaseAddress, locationAddressLabel } = this.parent;
      return (
        hasRealCoordinates(location) &&
        transportBaseAddress === locationAddressLabel
      );
    }
  );

const optionalBaseChargeSchema = Yup.number()
  .nullable()
  .optional()
  .transform((value, original) => (original === '' ? null : value))
  .typeError('Must be a number')
  .min(0, 'Must be 0 or greater');

// Price Per Mile specific schema
const mileageSchema = baseSchema.shape({
  location: pickedTransportBaseLocationSchema,
  pricePerMile: Yup.number()
    .required('Price per mile is required')
    .typeError('Must be a number')
    .moreThan(0, 'Must be greater than 0'),
  baseCharge: optionalBaseChargeSchema,
  minimumMiles: Yup.number()
    .nullable()
    .optional()
    .transform((value, original) => (original === '' ? null : value))
    .typeError('Must be a number')
    .min(0, 'Must be 0 or greater'),
  maximumMiles: Yup.number()
    .nullable()
    .optional()
    .transform((value, original) => (original === '' ? null : value))
    .typeError('Must be a number')
    .moreThan(0, 'Must be greater than 0')
    .test(
      'maximum-above-minimum',
      'Maximum miles must be greater than minimum miles',
      function (maximumMiles) {
        const { minimumMiles } = this.parent;
        if (
          maximumMiles === null ||
          maximumMiles === undefined ||
          minimumMiles === null ||
          minimumMiles === undefined
        ) {
          return true;
        }
        return maximumMiles >= minimumMiles;
      }
    ),
  maximumLoadKg: Yup.number()
    .required('Maximum load (kg) is required')
    .typeError('Must be a number')
    .moreThan(0, 'Must be greater than 0'),
  effectiveFrom: Yup.string().nullable().optional(),
  effectiveTo: Yup.string()
    .nullable()
    .optional()
    .test(
      'effective-to-after-from',
      'Effective to date must be on or after the effective from date',
      function (effectiveTo) {
        const { effectiveFrom } = this.parent;
        if (!effectiveTo || !effectiveFrom) return true;
        return effectiveTo >= effectiveFrom;
      }
    ),
});

// Upload file schema for Postcode and Additional FTL
const uploadSchema = baseSchema.shape({
  uploadFile: Yup.mixed(),
});

const mileageUploadSchema = uploadSchema.shape({
  location: pickedTransportBaseLocationSchema,
  baseCharge: optionalBaseChargeSchema,
  maximumLoadKg: Yup.number()
    .required('Maximum load (kg) is required')
    .typeError('Must be a number')
    .moreThan(0, 'Must be greater than 0'),
});

export type TariffFormValues = {
  tariffType: { label: string; value: string };
  vehicleType: { label: string; value: string };
  transportBaseAddress: string;
  location: { lon: number; lat: number };
  locationAddressLabel?: string;
  currency: { label: string; value: string };
  // Day Rate fields
  chargeForEightHourDay?: number;
  hourlyChargeOverEightHours?: number;
  hourlyChargeOverTenHours?: number;
  // Additional Load Charge fields
  rateMode?: 'pallet' | 'weight';
  palletRates?: { pallets: number; price?: number }[];
  weightRates?: { minWeight: number; maxWeight?: number | null; price?: number }[];
  maxOffRouteDistance?: number;
  chargePerAdditionalDrop?: number;
  standardIncludedLoadingMinutes?: number;
  standardIncludedUnloadingMinutes?: number;
  chargePerAdditionalHour?: number;
  chargePerOvertimeHour?: number;
  // Price Per Mile fields
  pricePerMile?: number;
  baseCharge?: number | null;
  minimumMiles?: number | null;
  maximumMiles?: number | null;
  maximumLoadKg?: number;
  willCollectFromAnywhere?: boolean;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  // Upload file for Postcode
  uploadFile?: File;
};

export function AddTariff() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authHttp = useAuthHttp();
  const authHttpFormData = useAuthHttp('multipart/form-data');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [addressOptions, setAddressOptions] = useState<
    { id: string; label: string; city?: string; country?: string }[]
  >([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<
    { message: string; row: number }[]
  >([]);

  // Get URL params for pre-filling
  const prefilledTariffType = searchParams.get('type');
  const prefilledVehicleType = searchParams.get('vehicleType');

  // Build vehicle type options from fleetMapping
  const vehicleTypeOptions = useMemo(
    () =>
      Object.entries(fleetMapping).map(([value, label]) => ({
        label,
        value,
      })),
    []
  );

  const defaultValues: TariffFormValues = {
    tariffType: prefilledTariffType
      ? TARIFF_TYPE_OPTIONS.find((t) => t.value === prefilledTariffType) ||
        TARIFF_TYPE_OPTIONS[0]
      : TARIFF_TYPE_OPTIONS[0],
    vehicleType: prefilledVehicleType
      ? vehicleTypeOptions.find((v) => v.value === prefilledVehicleType) ||
        vehicleTypeOptions[0]
      : vehicleTypeOptions[0],
    transportBaseAddress: '',
    location: { lon: 0, lat: 0 },
    locationAddressLabel: undefined,
    currency: CURRENCY_OPTIONS[0],
    // Day Rate defaults
    chargeForEightHourDay: undefined,
    hourlyChargeOverEightHours: undefined,
    hourlyChargeOverTenHours: undefined,
    // Additional Load Charge defaults
    rateMode: 'pallet',
    palletRates: DEFAULT_PALLET_RATES,
    weightRates: DEFAULT_WEIGHT_RATES,
    maxOffRouteDistance: undefined,
    chargePerAdditionalDrop: undefined,
    standardIncludedLoadingMinutes: undefined,
    standardIncludedUnloadingMinutes: undefined,
    chargePerAdditionalHour: undefined,
    chargePerOvertimeHour: undefined,
    // Price Per Mile defaults
    pricePerMile: undefined,
    baseCharge: undefined,
    minimumMiles: undefined,
    maximumMiles: undefined,
    maximumLoadKg: undefined,
    willCollectFromAnywhere: false,
    effectiveFrom: undefined,
    effectiveTo: undefined,
    // Upload file
    uploadFile: undefined,
  };

  const methods = useForm<TariffFormValues>({
    shouldUnregister: false,
    resolver: (values, context, options) => {
      const tariffType = values.tariffType?.value;
      let schema;
      switch (tariffType) {
        case 'Day Rate': schema = dayRateSchema; break;
        case 'Price Per Mile':
          schema = values.uploadFile ? mileageUploadSchema : mileageSchema; break;
        case 'Postcode':
        case 'Additional FTL': schema = uploadSchema; break;
        default: schema = baseSchema;
      }
      return yupResolver(schema)(values, context, options);
    },
    mode: 'onChange',
    defaultValues,
  });

  const { control, setValue, watch, reset, setError, clearErrors, getValues } =
    methods;
  const transportBaseLocationError = methods.formState.errors.location?.message;

  const watchTariffType = watch('tariffType');
  const watchVehicleType = watch('vehicleType');

  useDocumentTitle(`FLOX - New ${watchTariffType?.label || 'Transport'} Tariff`);

  // Clear validation errors when tariff type changes
  useEffect(() => {
    if (watchTariffType?.value) {
      clearErrors();
    }
  }, [watchTariffType?.value, clearErrors]);

  // Fetch address suggestions
  const handleFetchSuggestions = useCallback((input: string) => {
    if (input.length < 3) {
      setAddressOptions([]);
      return;
    }

    fetch(
      `https://autosuggest.search.hereapi.com/v1/autosuggest?apiKey=${HERE_APIKEY}&q=${input}&at=52.93175,12.77165`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.items) {
          const items = data.items.map(
            (item: {
              id: string;
              address: { label: string; city?: string; countryName?: string };
            }) => ({
              id: item.id,
              label: item.address.label,
              city: item.address.city,
              country: item.address.countryName,
            })
          );
          setAddressOptions(items);
        }
      })
      .catch((error) => {
        console.error('Error fetching suggestions:', error);
      });
  }, []);

  // Handle address selection
  const handleAddressSelect = useCallback(
    async (option: { id: string; label: string } | null) => {
      if (!option) return;
      setValue('transportBaseAddress', option.label);
      try {
        const response = await fetch(
          `https://lookup.search.hereapi.com/v1/lookup?id=${option.id}&apiKey=${HERE_APIKEY}`
        );
        const data = await response.json();
        if (data?.position) {
          const { lat, lng: lon } = data.position;
          setValue('locationAddressLabel', option.label);
          setValue('location', { lat, lon }, { shouldValidate: true });
        }
      } catch (error) {
        console.error('Error fetching position:', error);
      }
    },
    [setValue]
  );

  // Get API endpoint based on tariff type
  const getApiEndpoint = (tariffType: string) => {
    switch (tariffType) {
      case 'Postcode':
        return POSTCODE_RATECARD_CREATE;
      case 'Day Rate':
        return DAILY_RATECARD_CREATE;
      case 'Additional FTL':
        return ADDITIONAL_FTL_RATECARD_CREATE;
      case 'Price Per Mile':
        return MILEAGE_RATECARD_CREATE;
      default:
        return POSTCODE_RATECARD_CREATE;
    }
  };

  // File download path for templates
  const fileDownloadPath = useMemo(() => {
    const tariffType =
      watchTariffType?.value?.toLowerCase().replace(/\s+/g, '_') || 'postcode';
    const vehicleType =
      watchVehicleType?.value?.toLowerCase().replace(/\s+/g, '_') || '';
    return `${tariffType}_${vehicleType}`;
  }, [watchTariffType?.value, watchVehicleType?.value]);

  // Handle form submission
  const onSubmit = async (data: TariffFormValues) => {
    setIsSubmitting(true);
    setHasUploadError(false);
    setUploadErrors([]);

    // Manual validation for tariff-specific fields
    const tariffType = data.tariffType.value;
    let hasValidationErrors = false;

    if (tariffType === 'Day Rate') {
      if (!data.chargeForEightHourDay || data.chargeForEightHourDay <= 0) {
        setError('chargeForEightHourDay', {
          type: 'manual',
          message:
            'Charge for 8 hour day is required and must be greater than 0',
        });
        hasValidationErrors = true;
      }
      if (
        !data.hourlyChargeOverEightHours ||
        data.hourlyChargeOverEightHours <= 0
      ) {
        setError('hourlyChargeOverEightHours', {
          type: 'manual',
          message:
            'Hourly charge over 8 hours is required and must be greater than 0',
        });
        hasValidationErrors = true;
      }
      if (
        !data.hourlyChargeOverTenHours ||
        data.hourlyChargeOverTenHours <= 0
      ) {
        setError('hourlyChargeOverTenHours', {
          type: 'manual',
          message:
            'Hourly charge over 10 hours is required and must be greater than 0',
        });
        hasValidationErrors = true;
      }
    }

    if (tariffType === 'Additional FTL') {
      const additionalFields = [
        { key: 'maxOffRouteDistance', label: 'Max distance off-route' },
        { key: 'chargePerAdditionalDrop', label: 'Charge per extra drop' },
        { key: 'standardIncludedLoadingMinutes', label: 'STD loading time' },
        { key: 'standardIncludedUnloadingMinutes', label: 'STD drop off time' },
        { key: 'chargePerAdditionalHour', label: 'Cost per hour' },
        { key: 'chargePerOvertimeHour', label: 'Overtime per hour' },
      ] as const;

      for (const { key, label } of additionalFields) {
        if (!data[key] || (data[key] as number) <= 0) {
          setError(key, {
            type: 'manual',
            message: `${label} is required and must be greater than 0`,
          });
          hasValidationErrors = true;
        }
      }

      const rates =
        data.rateMode === 'pallet' ? data.palletRates : data.weightRates;
      const hasAtLeastOneRate = rates?.some(
        (r) => r.price !== undefined && r.price > 0
      );
      if (!hasAtLeastOneRate) {
        setHasUploadError(true);
        setUploadErrors([
          { message: 'At least one rate must have a price greater than 0', row: 0 },
        ]);
        hasValidationErrors = true;
      }
    }

    if (hasValidationErrors) {
      setIsSubmitting(false);
      return;
    }

    try {
      const endpoint = getApiEndpoint(tariffType);

      let payload: any = {
        vehicleType: data.vehicleType.value,
        transportBaseAddress: data.transportBaseAddress,
        transportBaseLocation: {
          lat: data.location.lat,
          lon: data.location.lon,
        },
        chargeCurrency: data.currency.value,
      };

      if (tariffType === 'Day Rate') {
        payload = {
          rateCard: {
            ...payload,
            chargeForEightHourDay: data.chargeForEightHourDay,
            hourlyChargeOverEightHours: data.hourlyChargeOverEightHours,
            hourlyChargeOverTehnHours: data.hourlyChargeOverTenHours,
          }
        };
        await authHttp.post(endpoint, payload);
      } else if (tariffType === 'Price Per Mile') {
        if (uploadFile) {
          const formData = buildMileageRateSheetFormData({
            rateSheet: uploadFile,
            transportBaseAddress: data.transportBaseAddress,
            transportBaseLocation: data.location,
            rateCurrency: data.currency.value,
            baseCharge: data.baseCharge ?? 0,
            maximumLoadKg: data.maximumLoadKg,
            willCollectFromAnywhere: data.willCollectFromAnywhere,
          });
          await authHttpFormData.post(MILEAGE_RATECARD_IMPORT, formData);
        } else {
          payload = {
            rateCard: {
              ...payload,
              pricePerMile: data.pricePerMile,
              baseCharge: data.baseCharge ?? 0,
              minimumMiles: data.minimumMiles ?? null,
              maximumMiles: data.maximumMiles ?? null,
              maximumLoadKg: data.maximumLoadKg,
              willCollectFromAnywhere: data.willCollectFromAnywhere ?? false,
              effectiveFrom: data.effectiveFrom || null,
              effectiveTo: data.effectiveTo || null,
            }
          };
          await authHttp.post(endpoint, payload);
        }
      } else if (tariffType === 'Additional FTL') {
        const currency = data.currency.value;
        const palletRates =
          data.rateMode === 'pallet'
            ? (data.palletRates || [])
                .filter((r) => r.price !== undefined && r.price > 0)
                .map((r) => ({
                  pallets: r.pallets,
                  price: r.price,
                  currency,
                }))
            : [];
        const weightRates =
          data.rateMode === 'weight'
            ? (data.weightRates || [])
                .filter((r) => r.price !== undefined && r.price > 0)
                .map((r) => ({
                  minWeight: r.minWeight,
                  maxWeight: r.maxWeight ?? null,
                  price: r.price,
                  currency,
                }))
            : [];

        const additionalLoadPayload = {
          transportCardRate: {
            vehicleType: data.vehicleType.value,
            transportBaseAddress: data.transportBaseAddress,
            transportBaseLocation: {
              lat: data.location.lat,
              lon: data.location.lon,
            },
            additionalChargeCurrency: currency,
            maxOffRouteDistance: data.maxOffRouteDistance,
            maxOffRouteDistanceUnit: OFF_ROUTE_DISTANCE_UNIT,
            chargePerAdditionalDrop: data.chargePerAdditionalDrop,
            standardIncludedLoadingMinutes:
              data.standardIncludedLoadingMinutes,
            standardIncludedUnloadingMinutes:
              data.standardIncludedUnloadingMinutes,
            chargePerAdditionalHour: data.chargePerAdditionalHour,
            chargePerOvertimeHour: data.chargePerOvertimeHour,
            palletRates,
            weightRates,
          },
        };
        await authHttp.post(endpoint, additionalLoadPayload);
      } else if (uploadFile) {
        const formData = new FormData();
        formData.append('Rates', uploadFile); 
        formData.append('TransportBaseAddress', data.transportBaseAddress);
        formData.append('TransportBaseAddressLat', data.location.lat.toString());
        formData.append('TransportBaseAddressLon', data.location.lon.toString());
        formData.append('RateCurrency', data.currency.value);
        await authHttpFormData.post(endpoint, formData);
      }

      navigate('/transport-price-master');
    } catch (error: any) {
      console.error('Error submitting tariff:', error);
      if (error?.response?.data?.errors) {
        setHasUploadError(true);
        setUploadErrors(error.response.data.errors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = ['Tariff Details'];

  const pages = [
    { name: 'Home', href: '/', current: false },
    {
      name: 'Transport Tariffs',
      href: '/transport-price-master',
      current: false,
    },
    { name: `Add Tariff for ${watchVehicleType?.label || 'Vehicle'}`, href: '/tariff/add', current: true },
  ];

  // Get icon for tariff type
  const getTariffIcon = (tariffType: string) => {
    switch (tariffType) {
      case 'Postcode':
        return <MdLocationOn className='w-5 h-5' />;
      case 'Day Rate':
        return <MdAccessTime className='w-5 h-5' />;
      case 'Additional FTL':
      case 'Price Per Mile':
        return <MdLocalShipping className='w-5 h-5' />;
      default:
        return <MdLocationOn className='w-5 h-5' />;
    }
  };

  return (
    <FormPage<TariffFormValues>
      header={`New ${watchTariffType?.label || 'Transport'} Tariff`}
      steps={steps}
      pages={pages}
      methods={methods}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    >
      <div className='flex flex-col gap-6 max-w-4xl'>
        {/* Tariff Type and Vehicle Type Selection */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Controller
            name='tariffType'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                label='Tariff Type'
                options={TARIFF_TYPE_OPTIONS}
                selected={field.value}
                setSelected={field.onChange}
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />

          <Controller
            name='vehicleType'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                label='Vehicle Type'
                options={vehicleTypeOptions}
                selected={field.value}
                setSelected={field.onChange}
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />
        </div>

        {/* Tariff Info Banner */}
        <div className='flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
          <div className='p-2 bg-blue-100 rounded-lg'>
            {getTariffIcon(watchTariffType?.value)}
          </div>
          <div className='flex flex-col'>
            <span className='font-medium text-gray-900'>
              {watchTariffType?.label} for{' '}
              {watchVehicleType?.label || 'Selected Vehicle'}
            </span>
            <span className='text-sm text-gray-600 mt-1'>
              {watchTariffType?.value === 'Postcode' &&
                'The tariff covers prices for Point-to-Point deliveries, from the origin location in the region of the specified Transport Base to the destinations outlined in the postcode matrix below.'}
              {watchTariffType?.value === 'Additional FTL' &&
                'The tariff provides pricing for additional co-shipment deliveries, on top of an existing pre-planned route, and as per the load size matrix below.'}
              {watchTariffType?.value === 'Day Rate' &&
                'The tariff includes a charge for a full working day, starting and ending in the region of the specified Transport Base, with unlimited mileage included.'}
              {watchTariffType?.value === 'Price Per Mile' &&
                'The tariff charges a flat rate for every mile from collection to delivery. Set a minimum to guarantee a floor on short runs, and a maximum to stop the tariff being offered on collection-to-delivery journeys longer than you cover.'}
            </span>
          </div>
        </div>

        {/* Transport Base Address */}
        <Controller
          name='transportBaseAddress'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              label='Transport Base Address'
              options={addressOptions}
              value={field.value}
              onChange={(option) =>
                handleAddressSelect(
                  option as { id: string; label: string } | null
                )
              }
              onInputChange={(value) => {
                field.onChange(value);
                handleFetchSuggestions(value);
              }}
              isInvalid={!!error || !!transportBaseLocationError}
              validationMessage={error?.message ?? transportBaseLocationError}
              placeholder='Start typing an address...'
            />
          )}
        />

        {/* Currency Selection */}
        <div className='w-full md:w-1/3'>
          <Controller
            name='currency'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                label='Currency'
                options={CURRENCY_OPTIONS}
                selected={field.value}
                setSelected={field.onChange}
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />
        </div>

        {/* Day Rate Fields */}
        {watchTariffType?.value === 'Day Rate' && (
          <div className='flex flex-col gap-6'>
            <h3 className='text-lg font-semibold text-gray-900'>Day Rates</h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <Controller
                name='chargeForEightHourDay'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Charge for 8 hour day'
                    name='chargeForEightHourDay'
                    type='number'
                    placeholder='0.00'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />

              <Controller
                name='hourlyChargeOverEightHours'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Hourly charge over 8 hours'
                    name='hourlyChargeOverEightHours'
                    type='number'
                    placeholder='0.00'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />

              <Controller
                name='hourlyChargeOverTenHours'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Hourly charge over 10 hours'
                    name='hourlyChargeOverTenHours'
                    type='number'
                    placeholder='0.00'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />
            </div>
          </div>
        )}

        {/* Price Per Mile Fields */}
        {watchTariffType?.value === 'Price Per Mile' && (
          <div className='flex flex-col gap-6'>
            <h3 className='text-lg font-semibold text-gray-900'>
              Price Per Mile
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <Controller
                name='pricePerMile'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Price per mile'
                    name='pricePerMile'
                    type='number'
                    placeholder='0.00'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />

              <Controller
                name='baseCharge'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Base charge (optional)'
                    name='baseCharge'
                    type='number'
                    placeholder='0.00'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                    helperText='A fixed amount added once to every job, on top of the miles charged (including any minimum miles). Leave blank for none.'
                  />
                )}
              />

              <Controller
                name='maximumLoadKg'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Maximum load (kg)'
                    name='maximumLoadKg'
                    type='number'
                    placeholder='0'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                    helperText='The heaviest load this vehicle carries. Heavier loads get no quote from this rate.'
                  />
                )}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <Controller
                name='minimumMiles'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Minimum miles charged (optional)'
                    name='minimumMiles'
                    type='number'
                    placeholder='0'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />

              <Controller
                name='maximumMiles'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Maximum miles covered (optional)'
                    name='maximumMiles'
                    type='number'
                    placeholder='0'
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    isInvalid={!!error}
                    validationMessage={error?.message}
                    helperText='The longest collection-to-delivery journey this rate quotes for. It does not limit how far you travel to collect.'
                  />
                )}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <Controller
                name='effectiveFrom'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Effective from (optional)'
                    name='effectiveFrom'
                    type='date'
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value || undefined)}
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />

              <Controller
                name='effectiveTo'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Effective to (optional)'
                    name='effectiveTo'
                    type='date'
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value || undefined)}
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />
            </div>

            <Controller
              name='willCollectFromAnywhere'
              control={control}
              render={({ field }) => (
                <div className='flex flex-col gap-1'>
                  <Checkbox
                    label='Collect from anywhere'
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    name={field.name}
                    ref={field.ref}
                    aria-describedby='collect-from-anywhere-help'
                  />
                  <p
                    id='collect-from-anywhere-help'
                    className='text-sm text-gray-500'
                  >
                    Tick this if you collect at any distance from your base.
                    You&apos;re paid per mile from collection to delivery only,
                    not for travelling to the collection point. Leave it
                    unticked and this rate only quotes for collections within 50
                    km (about 30 miles) of your base.
                  </p>
                </div>
              )}
            />
          </div>
        )}

        {/* Additional Load Charge - Inline Form */}
        {watchTariffType?.value === 'Additional FTL' && (
          <LoadSizeMatrixFields
            control={control}
            watch={watch}
            rateError={
              hasUploadError
                ? uploadErrors[0]?.message || 'Please fix the errors below'
                : null
            }
          />
        )}

        {/* File Upload */}
        {(watchTariffType?.value === 'Postcode' ||
          watchTariffType?.value === 'Price Per Mile') && (
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-2'>
              <h3 className='text-lg font-semibold text-gray-900'>
                {watchTariffType?.value === 'Price Per Mile'
                  ? 'Upload a Rate Sheet'
                  : 'Postcode Matrix'}
              </h3>
              <p className='text-sm text-gray-600'>
                {watchTariffType?.value === 'Price Per Mile'
                  ? 'Instead of entering the rate above, you can upload the provided template. An uploaded file replaces the price, miles and dates above. Your base charge, maximum load and Collect from anywhere choice still apply.'
                  : 'Please use the provided template to input prices for the served postcode areas and per load size respectively.'}
              </p>
            </div>

            <div className='flex flex-col items-center gap-4 p-6 border border-gray-200 rounded-lg bg-gray-50'>
              <a
                href={
                  watchTariffType?.value === 'Price Per Mile'
                    ? '/files/price_per_mile_template.xlsx'
                    : `/files/${fileDownloadPath}.xlsx`
                }
                className='w-full max-w-md flex'
              >
                <Button
                  type='button'
                  label='Download Excel Template'
                  intent='primary'
                  startIcon={<MdDownload className='w-5 h-5' />}
                  onClick={() => {}}
                  className='flex-1'
                />
              </a>

              <span className='text-sm font-medium text-gray-500'>OR</span>

              {uploadFile ? (
                <div className='flex flex-col items-center gap-4 w-full max-w-md'>
                  <div className='flex items-center gap-2 text-gray-700'>
                    <MdUploadFile className='w-5 h-5' />
                    <span className='font-medium'>{uploadFile.name}</span>
                  </div>
                  <Button
                    type='button'
                    label='Clear Selected File'
                    intent='danger'
                    startIcon={<MdDelete className='w-5 h-5' />}
                    onClick={() => {
                      setUploadFile(null);
                      setHasUploadError(false);
                      setUploadErrors([]);
                    }}
                  />
                  {hasUploadError && (
                    <div className='flex flex-col gap-2 p-4 bg-red-50 border border-red-200 rounded-lg w-full'>
                      <span className='font-medium text-red-700'>
                        Error uploading file. Please fix the following errors:
                      </span>
                      {uploadErrors.map((error) => (
                        <span
                          key={`${error.row}-${error.message}`}
                          className='text-sm text-red-600'
                        >
                          Row {error.row}: {error.message}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className='w-full max-w-md'>
                  <Dropzone
                    label='Upload Tariff Matrix'
                    optional
                    onDrop={(file: File | null) => {
                      if (file) {
                        setUploadFile(file);
                        setValue('uploadFile', file);
                      }
                    }}
                    fileTypesTagline='Excel files (.xlsx, .xls)'
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </FormPage>
  );
}
